import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Per-route SEO metadata in 3 languages (lt primary).
// Picks the best title/description for the current path so search engines and social
// previews get a meaningful snippet even on a SPA.
const ROUTE_META = {
  lt: {
    "/": {
      title: "StopChlamas – Skelbimai garažo daiktams visoje Lietuvoje | NEMOKAMA",
      desc: "Lietuviška skelbimų platforma įrankiams, auto detalėms, sodo technikai ir visam tam, kas slypi tavo garaže. Įkelti skelbimą NEMOKAMA. 24h aukcionai, atiduodu/mainau sekcija, filtras pagal miestą.",
    },
    "/browse": {
      title: "Visi skelbimai – įrankiai, auto detalės, sodo technika | StopChlamas",
      desc: "Naršyk tūkstančius skelbimų visoje Lietuvoje. Filtruok pagal miestą, kategoriją, kainą. Įrankiai, padangos, sodo įranga, auto detalės — visa garažo prekyba vienoje vietoje.",
    },
    "/post": {
      title: "Įkelti skelbimą NEMOKAMAI | StopChlamas",
      desc: "Įkelk savo įrankius, auto detales ar sodo techniką į StopChlamas. Pirmas ir kiekvienas skelbimas — visiškai nemokamas. Prikabink iki 6 nuotraukų, parduok arba atiduok.",
    },
    "/about": {
      title: "Apie StopChlamas – Lietuviška garažo skelbimų platforma",
      desc: "Sužinok, kodėl StopChlamas atsirado ir kuo skiriasi nuo Vinted ar eBay. Lokali platforma įrankiams, auto detalėms ir kitam garažo turiniui.",
    },
    "/contact": {
      title: "Kontaktai | StopChlamas",
      desc: "Susisiek su StopChlamas komanda. Klausimai, pasiūlymai, pranešimai apie netinkamą turinį – atsakome per 48 valandas.",
    },
    "/terms": {
      title: "Naudojimosi sąlygos | StopChlamas",
      desc: "StopChlamas naudojimosi sąlygos. Vartotojo teisės, draudžiamos prekės, sandorių taisyklės, aukcionų sąlygos.",
    },
    "/privacy": {
      title: "Privatumo politika (BDAR) | StopChlamas",
      desc: "Kaip StopChlamas tvarko tavo asmens duomenis pagal ES BDAR reglamentą. Kokius duomenis renkame, kodėl ir kokias turi teises.",
    },
  },
  ru: {
    "/": {
      title: "StopChlamas – Объявления для гаражных вещей | БЕСПЛАТНО",
      desc: "Литовская площадка объявлений для инструментов, автозапчастей, садовой техники и всего, что лежит в твоём гараже. БЕСПЛАТНО. Аукционы 24ч, отдаю/меняю, фильтр по городу.",
    },
    "/browse": {
      title: "Все объявления – инструменты, автозапчасти, садовая техника | StopChlamas",
      desc: "Просматривай тысячи объявлений по всей Литве. Фильтр по городу, категории, цене. Инструменты, шины, садовая техника — всё в одном месте.",
    },
    "/post": {
      title: "Подать объявление БЕСПЛАТНО | StopChlamas",
      desc: "Размести инструменты, автозапчасти или садовую технику на StopChlamas. Каждое объявление — бесплатно. До 6 фото.",
    },
    "/about": {
      title: "О StopChlamas – Литовская гаражная площадка",
      desc: "Узнай, почему появился StopChlamas и чем он отличается от Vinted и eBay. Локальная площадка для инструментов и автозапчастей.",
    },
    "/contact": {
      title: "Контакты | StopChlamas",
      desc: "Свяжись с командой StopChlamas. Вопросы, предложения, жалобы — ответим в течение 48 часов.",
    },
    "/terms": {
      title: "Условия использования | StopChlamas",
      desc: "Условия использования StopChlamas. Права пользователя, запрещённые товары, правила сделок и аукционов.",
    },
    "/privacy": {
      title: "Политика конфиденциальности (GDPR) | StopChlamas",
      desc: "Как StopChlamas обрабатывает твои персональные данные согласно GDPR ЕС. Какие данные собираем, зачем, и какие у тебя права.",
    },
  },
  en: {
    "/": {
      title: "StopChlamas – Garage stuff classifieds for Lithuania | FREE",
      desc: "Lithuanian marketplace for tools, auto parts, garden equipment and everything stashed in your garage. Free listings, 24-hour auctions, give-away/trade section, city-based filtering.",
    },
    "/browse": {
      title: "Browse listings – tools, auto parts, garden equipment | StopChlamas",
      desc: "Browse thousands of listings across Lithuania. Filter by city, category, price. Tools, tires, garden gear and auto parts in one place.",
    },
    "/post": {
      title: "Post a listing for FREE | StopChlamas",
      desc: "Sell your tools, auto parts or garden equipment on StopChlamas. Every listing is 100% free. Add up to 6 photos.",
    },
    "/about": {
      title: "About StopChlamas – Lithuania’s garage marketplace",
      desc: "Find out why StopChlamas exists and how it’s different from Vinted or eBay. A local marketplace for tools, auto parts and the rest of your garage.",
    },
    "/contact": {
      title: "Contact | StopChlamas",
      desc: "Get in touch with the StopChlamas team. Questions, suggestions, content reports — we reply within 48 hours.",
    },
    "/terms": {
      title: "Terms of Service | StopChlamas",
      desc: "StopChlamas Terms of Service. User rights, prohibited items, transaction rules, auction terms.",
    },
    "/privacy": {
      title: "Privacy Policy (GDPR) | StopChlamas",
      desc: "How StopChlamas processes your personal data under EU GDPR. What we collect, why, and your rights.",
    },
  },
};

const HTML_LOCALE = { lt: "lt", ru: "ru", en: "en" };

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export default function useSEO(lang) {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    // Dynamic SEO landing pages set their own title/description from the
    // LandingPage component (because they depend on resolved city/category
    // names from the API). Skip the static fallback here so we don't clobber
    // their per-route values.
    const isLanding =
      path.startsWith("/skelbimai/") ||
      path.startsWith("/kategorija/") ||
      path.startsWith("/atiduodu") ||
      path.startsWith("/aukcionai");    if (isLanding) {
      // Still keep the <html lang> in sync — the landing page doesn't touch it.
      document.documentElement.setAttribute("lang", HTML_LOCALE[lang] || "lt");
      return;
    }
    const langMap = ROUTE_META[lang] || ROUTE_META.lt;
    // Normalise dynamic listing detail to root meta (we only have static defaults)
    const key =
      langMap[path] !== undefined
        ? path
        : path.startsWith("/listing/")
        ? "/"
        : "/";
    const meta = langMap[key] || ROUTE_META.lt["/"];

    document.title = meta.title;
    setMeta('meta[name="description"]', "content", meta.desc);
    setMeta('meta[name="title"]', "content", meta.title);
    setMeta('meta[property="og:title"]', "content", meta.title);
    setMeta('meta[property="og:description"]', "content", meta.desc);
    setMeta('meta[name="twitter:title"]', "content", meta.title);
    setMeta('meta[name="twitter:description"]', "content", meta.desc);

    // Canonical: full URL (uses the live origin)
    const canonical = document.querySelector('link[rel="canonical"]');
    const fullUrl = `${window.location.origin}${path === "/" ? "/" : path}`;
    if (canonical) canonical.setAttribute("href", fullUrl);
    setMeta('meta[property="og:url"]', "content", fullUrl);
    setMeta('meta[name="twitter:url"]', "content", fullUrl);

    // <html lang>
    document.documentElement.setAttribute("lang", HTML_LOCALE[lang] || "lt");
  }, [lang, location.pathname]);
}
